import { ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
	SidebarGroup,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	useSidebar,
} from '@/components/ui/sidebar';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import useActiveTab from '@/hooks/useActiveTab';
import { cn } from '@/lib/utils';
import type { TMainMenu } from './menuItems';
import { useSidebarContext } from './Provider/ProviderSideBar';

function OverflowTitle({
	title,
	className = '',
}: {
	title: string;
	className?: string;
}) {
	const titleRef = useRef<HTMLSpanElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	const updateOverflow = useCallback(() => {
		const element = titleRef.current;
		if (!element) return;
		setIsOverflowing(element.scrollWidth > element.clientWidth + 1);
	}, []);

	useEffect(() => {
		updateOverflow();
		const animationFrame = window.requestAnimationFrame(updateOverflow);
		const element = titleRef.current;

		if (!element || typeof ResizeObserver === 'undefined') {
			window.addEventListener('resize', updateOverflow);
			return () => {
				window.cancelAnimationFrame(animationFrame);
				window.removeEventListener('resize', updateOverflow);
			};
		}

		const resizeObserver = new ResizeObserver(updateOverflow);
		resizeObserver.observe(element);
		if (element.parentElement) resizeObserver.observe(element.parentElement);

		return () => {
			window.cancelAnimationFrame(animationFrame);
			resizeObserver.disconnect();
		};
	}, [title, updateOverflow]);

	const label = (
		<span
			ref={titleRef}
			className={cn(
				'block min-w-0 flex-1 truncate whitespace-nowrap',
				className,
			)}
		>
			{title}
		</span>
	);

	if (!isOverflowing) return label;

	return (
		<Tooltip>
			<TooltipTrigger render={label} />
			<TooltipContent
				side="right"
				align="center"
				className="z-[9999] max-w-[18rem] rounded-lg border border-[#E4E4E7] bg-white px-3 py-2 text-xs font-medium text-[#18181B] shadow-lg"
			>
				{title}
			</TooltipContent>
		</Tooltip>
	);
}

type MenuPathEntry = { item: TMainMenu; key: string };

function getAlphaMarker(index: number, uppercase = false) {
	let value = index + 1;
	let label = '';
	const baseCode = uppercase ? 65 : 97;
	while (value > 0) {
		value -= 1;
		label = String.fromCharCode(baseCode + (value % 26)) + label;
		value = Math.floor(value / 26);
	}
	return label;
}

function getMenuMarker(index: number, depth: number) {
	if (depth === 1) return getAlphaMarker(index, true);
	if (depth === 2) return String(index + 1);
	return getAlphaMarker(index);
}

function MenuMarker({
	marker,
	depth,
	isActive,
}: {
	marker: string;
	depth: number;
	isActive: boolean;
}) {
	return (
		<span
			className={cn(
				'flex shrink-0 items-center justify-center font-semibold leading-none transition-colors duration-150',
				depth === 1 &&
					'h-[18px] min-w-[18px] rounded-[5px] border border-[#E4E4E7] bg-[#FAFAFA] px-1 text-[9.5px] text-[#52525B]',
				depth === 2 &&
					'h-[17px] min-w-[17px] rounded-full border border-transparent bg-[#F4F4F5] px-1 text-[9.5px] text-[#71717A]',
				depth > 2 &&
					'h-[17px] min-w-[17px] rounded-[4px] border border-[#E4E4E7] bg-white px-1 text-[9.5px] text-[#71717A]',
				isActive &&
					'border-[#CFCBFF] bg-[#F6F5FF] text-[#352EFF] shadow-[0_1px_2px_rgba(53,46,255,0.10)]',
			)}
		>
			{marker}
		</span>
	);
}

export function NavMain({ items }: { items: TMainMenu[] }) {
	const { expanded, toggleExpand, setExpanded } = useSidebarContext();
	const { activeTab, setActiveTab } = useActiveTab();
	const { state: sidebarState } = useSidebar();
	const navigate = useNavigate();
	const location = useLocation();

	const getMenuKey = useCallback(
		(item: TMainMenu, index: number, parentKey = '') =>
			`${parentKey}/${index}:${item.title}:${item.url || ''}`,
		[],
	);

	const isRouteActive = useCallback(
		(route?: string) => {
			if (!route || route === '#') return false;
			if (matchPath({ path: route, end: true }, location.pathname)) return true;
			if (route === '/') return false;
			return Boolean(
				matchPath(
					{ path: route.replace(/\/\*$/, '/*'), end: false },
					location.pathname,
				),
			);
		},
		[location.pathname],
	);

	const findMenuPath = useCallback(
		(
			menuItems: TMainMenu[],
			predicate: (item: TMainMenu, key: string) => boolean,
			parentKey = '',
			trail: MenuPathEntry[] = [],
		): MenuPathEntry[] | null => {
			for (const [index, item] of menuItems.entries()) {
				const key = getMenuKey(item, index, parentKey);
				const nextTrail = [...trail, { item, key }];
				if (predicate(item, key)) return nextTrail;
				if (item.items?.length) {
					const matched = findMenuPath(item.items, predicate, key, nextTrail);
					if (matched) return matched;
				}
			}
			return null;
		},
		[getMenuKey],
	);

	const routeMatchedPath = useMemo(
		() => findMenuPath(items, (item) => isRouteActive(item.url)),
		[items, findMenuPath, isRouteActive],
	);
	const routeMatchedItem = routeMatchedPath?.[routeMatchedPath.length - 1];
	const activeMenuKey = routeMatchedItem?.key || activeTab;

	const expandMenuPath = useCallback(
		(path: MenuPathEntry[] | null) => {
			if (!path?.length) return;
			const expandableItems = path.filter(({ item }) => item.items?.length);
			if (!expandableItems.length) return;
			setExpanded((prev) => {
				let hasChanged = false;
				const next = { ...prev };
				for (const { key } of expandableItems) {
					if (!next[key]) {
						next[key] = true;
						hasChanged = true;
					}
				}
				return hasChanged ? next : prev;
			});
		},
		[setExpanded],
	);

	useEffect(() => {
		if (routeMatchedItem && routeMatchedItem.key !== activeTab) {
			setActiveTab(routeMatchedItem.key);
		}
		expandMenuPath(routeMatchedPath);
	}, [
		activeTab,
		routeMatchedItem,
		routeMatchedPath,
		setActiveTab,
		expandMenuPath,
	]);

	useEffect(() => {
		if (!routeMatchedPath && activeTab) {
			expandMenuPath(findMenuPath(items, (_item, key) => key === activeTab));
		}
	}, [items, activeTab, routeMatchedPath, findMenuPath, expandMenuPath]);

	const toggleMenu = (
		menuKey: string,
		url: string,
		event?: React.MouseEvent | React.KeyboardEvent,
		isTopLevel = false,
	) => {
		if (event) {
			event.stopPropagation();
			if ('key' in event && event.key !== 'Enter' && event.key !== ' ') return;
		}
		if (url) navigate(url);
		if (isTopLevel) {
			setExpanded((prev) => {
				const newState = { ...prev };
				for (const [index, item] of items.entries()) {
					const itemKey = getMenuKey(item, index);
					if (itemKey !== menuKey) newState[itemKey] = false;
				}
				newState[menuKey] = !prev[menuKey];
				return newState;
			});
		} else {
			toggleExpand(menuKey);
		}
	};

	const handleSetActiveTab = (item: TMainMenu, key: string) => {
		if (!item.items?.length || isRouteActive(item.url)) {
			setActiveTab(key);
		}
	};

	const renderMenuSubItems = (
		menuItems: TMainMenu[],
		depth = 1,
		parentKey = '',
	) => (
		<SidebarMenuSub
			className={cn(
				'mx-0 gap-0.5 border-l border-[#E7E7EC] py-1.5 pr-0 pl-3',
				depth === 1 ? 'ml-4' : 'ml-3',
			)}
		>
			{menuItems.map((item, index) => {
				const itemKey = getMenuKey(item, index, parentKey);
				const hasChildren = Boolean(item.items?.length);
				const itemExactActive = itemKey === activeMenuKey;
				const marker = getMenuMarker(index, depth);

				if (hasChildren) {
					return (
						<Collapsible
							key={itemKey}
							asChild
							open={expanded[itemKey] ?? false}
							className="group/collapsible"
						>
							<SidebarMenuSubItem className="relative">
								<CollapsibleTrigger asChild>
									<SidebarMenuSubButton
										onClick={(event) => {
											toggleMenu(itemKey, item.url || '', event);
											handleSetActiveTab(item, itemKey);
										}}
										className={cn(
											'h-8 w-full rounded-md border border-transparent px-2 text-[12.75px] font-medium text-[#52525B] transition-[background-color,border-color,color,box-shadow] duration-150 hover:border-[#ECECF0] hover:bg-[#FAFAFA] hover:text-[#18181B]',
											itemExactActive &&
												'border-[#DEDBFF] bg-white font-semibold text-[#352EFF] shadow-[inset_2px_0_0_#352EFF,0_1px_2px_rgba(24,24,27,0.04)] hover:border-[#DEDBFF] hover:bg-white hover:text-[#352EFF]',
										)}
									>
										<MenuMarker
											marker={marker}
											depth={depth}
											isActive={itemExactActive}
										/>
										<OverflowTitle title={item.title} />
										<ChevronRight
											className={cn(
												'ml-auto size-4 shrink-0 text-[#A1A1AA] transition-transform duration-200',
												expanded[itemKey] && 'rotate-90',
												itemExactActive && 'text-[#352EFF]',
											)}
										/>
									</SidebarMenuSubButton>
								</CollapsibleTrigger>
								<CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
									{renderMenuSubItems(
										item.items as TMainMenu[],
										depth + 1,
										itemKey,
									)}
								</CollapsibleContent>
							</SidebarMenuSubItem>
						</Collapsible>
					);
				}

				return (
					<SidebarMenuSubItem
						key={itemKey}
						onClick={(event) => {
							event.stopPropagation();
							navigate(item.url || '');
							handleSetActiveTab(item, itemKey);
						}}
						className="relative"
					>
						<SidebarMenuSubButton
							className={cn(
								'h-8 w-full cursor-pointer rounded-md border border-transparent px-2 text-[12.75px] font-medium text-[#52525B] transition-[background-color,border-color,color,box-shadow] duration-150 hover:border-[#ECECF0] hover:bg-[#FAFAFA] hover:text-[#18181B]',
								itemExactActive &&
									'border-[#DEDBFF] bg-white font-semibold text-[#352EFF] shadow-[inset_2px_0_0_#352EFF,0_1px_2px_rgba(24,24,27,0.04)] hover:border-[#DEDBFF] hover:bg-white hover:text-[#352EFF]',
							)}
							aria-current={itemExactActive ? 'page' : undefined}
						>
							<MenuMarker
								marker={marker}
								depth={depth}
								isActive={itemExactActive}
							/>
							<OverflowTitle title={item.title} />
						</SidebarMenuSubButton>
					</SidebarMenuSubItem>
				);
			})}
		</SidebarMenuSub>
	);

	return (
		<SidebarGroup className="overflow-y-auto px-2.5 py-3.5 no-scrollbar">
			<SidebarGroupLabel className="h-7 px-2.5 text-[10.5px] font-semibold tracking-[0.1em] text-[#7C7C86]">
				Platform
			</SidebarGroupLabel>

			<SidebarMenu className="gap-1">
				{items.map((item, index) => {
					const itemKey = getMenuKey(item, index);
					const isExactActive = itemKey === activeMenuKey;
					const isSectionEngaged =
						isExactActive ||
						expanded[itemKey] ||
						activeMenuKey?.startsWith(`${itemKey}/`);

					return (
						<Collapsible
							key={itemKey}
							asChild
							open={expanded[itemKey] ?? false}
							className="group/collapsible"
						>
							<SidebarMenuItem>
								<CollapsibleTrigger asChild>
									<SidebarMenuButton
										tooltip={
											sidebarState === 'collapsed' ? item.title : undefined
										}
										className={cn(
											'relative h-[38px] rounded-lg border border-transparent px-2 text-[13.5px] font-medium text-[#3F3F46] transition-[background-color,border-color,color,box-shadow] duration-150 hover:border-[#E7E7EC] hover:bg-white hover:text-[#18181B] hover:shadow-[0_1px_2px_rgba(24,24,27,0.04)] focus-visible:ring-2 focus-visible:ring-[#352EFF]/20 group-data-[collapsible=icon]:h-9 group-data-[collapsible=icon]:w-9 group-data-[collapsible=icon]:rounded-lg group-data-[collapsible=icon]:px-2',
											isExactActive &&
												'border-[#D8D5FF] bg-white font-semibold text-[#352EFF] shadow-[inset_3px_0_0_#352EFF,0_1px_3px_rgba(24,24,27,0.06)] hover:border-[#D8D5FF] hover:bg-white hover:text-[#352EFF]',
										)}
										aria-current={isExactActive ? 'page' : undefined}
										onClick={(e) => {
											toggleMenu(itemKey, item.url || '', e, true);
											handleSetActiveTab(item, itemKey);
										}}
									>
										{item.icon && (
											<span
												className={cn(
													'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[#E4E4E7] bg-[#FAFAFA] text-[#71717A] transition-colors duration-150 group-data-[collapsible=icon]:h-5 group-data-[collapsible=icon]:w-5 group-data-[collapsible=icon]:border-transparent',
													isSectionEngaged &&
														'border-[#D8D5FF] bg-[#F6F5FF] text-[#352EFF]',
												)}
											>
												<item.icon className="size-[15px] shrink-0" />
											</span>
										)}

										<OverflowTitle title={item.title} />

										{item.items && item.items.length > 0 ? (
											<ChevronRight
												className={cn(
													'ml-auto size-4 shrink-0 text-[#A1A1AA] transition-transform duration-200',
													expanded[itemKey] && 'rotate-90',
													isExactActive && 'text-[#352EFF]',
												)}
											/>
										) : null}
									</SidebarMenuButton>
								</CollapsibleTrigger>

								{item.items && item.items.length > 0 && (
									<CollapsibleContent className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
										{renderMenuSubItems(item.items, 1, itemKey)}
									</CollapsibleContent>
								)}
							</SidebarMenuItem>
						</Collapsible>
					);
				})}
			</SidebarMenu>
		</SidebarGroup>
	);
}
